/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Reading a geofence's boundary out of an audit record's whole-field snapshot.
 *
 * The diff alone cannot describe a polygon edit. It restates only the vertices that moved, so
 * dragging one corner of an eight-point fence yields one pair of coordinates — and a "polygon"
 * assembled from those is a shape the user never drew. Records written since the audit store gained
 * `metadata` carry the entire point array from both sides, which is the only source a boundary
 * should ever be drawn from.
 *
 * Older records have no snapshot and never will. For those the caller falls back to plotting the
 * individual vertices the diff does name — see `GeofenceRenderer`.
 */

import type { LatLng } from './GeofenceDiffMap'

import type { AuditMetadata } from '../../types'

/** The field the geofence producer asks the audit store to keep whole. */
const BOUNDARY_FIELD = 'geofencepoints'

/** Fewer than three vertices is not a shape, whatever the record claims. */
const MIN_POLYGON_POINTS = 3

export interface BoundarySnapshot {
  /** The complete boundary before the edit. Empty when the fence had none. */
  before: LatLng[]
  /** The complete boundary after the edit. Empty when the fence has none. */
  after: LatLng[]
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const asNumber = (value: unknown): number | undefined => (typeof value === 'number' ? value : undefined)

/**
 * Turn one side of the snapshot into an ordered ring.
 *
 * `pointIndex` is the boundary's own ordering and is what the shape depends on — array order is a
 * serialisation detail that has no reason to match. Where a point carries no index we fall back to
 * its position rather than dropping it, so a malformed record degrades to "probably right" instead
 * of a hole in the outline.
 */
const toRing = (value: unknown): LatLng[] => {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((point, position) => {
      if (!isRecord(point)) {
        return undefined
      }

      const lat = asNumber(point.latitude)
      const lng = asNumber(point.longitude)
      if (lat === undefined || lng === undefined) {
        return undefined
      }

      return { order: asNumber(point.pointIndex) ?? position, lat, lng }
    })
    .filter((point): point is { order: number; lat: number; lng: number } => point !== undefined)
    .sort((a, b) => a.order - b.order)
    .map(({ lat, lng }) => ({ lat, lng }))
}

/**
 * The before and after boundaries, or `undefined` when the record cannot supply a drawable one.
 *
 * Undefined is the honest answer for every pre-snapshot record, and the caller treats it as "do not
 * draw a polygon" rather than reaching back into the diff for something shaped like one.
 */
export const readBoundarySnapshot = (metadata: AuditMetadata | undefined): BoundarySnapshot | undefined => {
  const snapshot = metadata?.snapshots[BOUNDARY_FIELD]
  if (!snapshot) {
    return undefined
  }

  const before = toRing(snapshot.old)
  const after = toRing(snapshot.new)

  // One side may legitimately be empty — a boundary added or cleared outright — but if neither side
  // is a shape there is nothing here worth a map.
  if (before.length < MIN_POLYGON_POINTS && after.length < MIN_POLYGON_POINTS) {
    return undefined
  }

  return { before, after }
}

/**
 * Whether the boundary changed but was too large for the audit store to keep.
 *
 * Worth saying out loud: the reader is looking at a geofence edit with no map, and "we chose not to
 * store it" is a different answer from "this record predates snapshots".
 */
export const isBoundaryTruncated = (metadata: AuditMetadata | undefined): boolean =>
  metadata?.truncated?.includes(BOUNDARY_FIELD) ?? false
